import { useCallback } from 'react';
import { FormField, ContactFormConfig } from '../types/ContactForm';
import { generateUniqueFieldName } from '../helpers/fieldNameGenerator';

interface UseFieldOperationsProps {
    config: ContactFormConfig;
    updateConfig: (property: string, value: unknown) => void;
}

export const useFieldOperations = ({ config, updateConfig }: UseFieldOperationsProps) => {
    const updateField = useCallback((index: number, property: string, value: unknown) => {
        const updatedFields = config.formFields.map((field, i) =>
            i === index ? { ...field, [property]: value } : field
        );
        updateConfig('formFields', updatedFields);
    }, [config.formFields, updateConfig]);

    const addField = useCallback((step?: number) => {
        const defaultLabel = 'Neues Feld';
        const uniqueName = generateUniqueFieldName(defaultLabel, config.formFields);

        const newField: FormField = {
            id: Date.now().toString(),
            type: 'text',
            label: defaultLabel,
            name: uniqueName,
            placeholder: '',
            required: false,
            width: 100,
            step: step || 1
        };
        updateConfig('formFields', [...config.formFields, newField]);
    }, [config.formFields, updateConfig]);

    const removeField = useCallback((index: number) => {
        const updatedFields = config.formFields.filter((_, i) => i !== index);
        updateConfig('formFields', updatedFields);
    }, [config.formFields, updateConfig]);

    const moveField = useCallback((index: number, direction: 'up' | 'down') => {
        const fields = [...config.formFields];
        const newIndex = direction === 'up' ? index - 1 : index + 1;

        if (newIndex >= 0 && newIndex < fields.length) {
            [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
            updateConfig('formFields', fields);
        }
    }, [config.formFields, updateConfig]);

    return {
        updateField,
        addField,
        removeField,
        moveField
    };
};


